import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { IspPage } from './ispLayout';
import IspPager, { PAGE_SIZE } from './IspPager';
import { useIspGate } from './useIspGate';
import { RowActions, actionColumn } from './ispSettingsUI';
import {
   createIspPayment,
   type IspPaymentDraft,
   updateIspAccount,
   getIspAccounts,
   getIspCustomers,
   getIspPlans,
   getIspServers,
   createIspAccount,
   suspendIspAccount,
   resumeIspAccount,
   disconnectIspAccount,
   type IspAccountRow,
   type IspAccountDraft,
} from '@/api/isp';

/**
 * ISP Business — Accounts.
 *
 * An ACCOUNT: one plan, on one router, for one customer. Aztech's word, and
 * the reason it is not "subscriber" — a restaurant owner with Resto 1 and Resto
 * 2 is ONE customer with TWO accounts (Ray, 2026-08-28).
 *
 * ─── THREE WAYS TO STOP A LINE, AND THEY ARE NOT THE SAME ───
 *   Suspend      the profile is swapped, the secret STAYS. They are coming back.
 *   Disconnect   the secret is removed, the history STAYS. They have gone.
 *   Delete       not offered here at all.
 *
 * Deleting would take the invoices and payments with it, so an operator chasing
 * an old debt — or proving one was settled — would have nothing. Every screen in
 * this product pushes the reversible option, because billing history is the
 * thing an ISP cannot recreate.
 */
const STATUS_LABEL: Record<IspAccountRow['status'], string> = {
   waiting: 'Not on the router yet',
   active: 'Active',
   suspended: 'Suspended',
   disconnected: 'Disconnected',
};

type Filter = 'all' | IspAccountRow['status'];

const IspAccounts: React.FC = () => {
   const qc = useQueryClient();
   const { handleShow, handleClose } = useUIStore();
   const { gate } = useIspGate();
   const [filter, setFilter] = useState<Filter>('all');
   const [query, setQuery] = useState('');

   const { data: page, isLoading } = useQuery({
      queryKey: ['isp-accounts'],
      queryFn: () => getIspAccounts(),
   });
   const accounts = page?.accounts ?? [];
   /** How many of the three lifetime free subscribers are left. */
   const freeLeft = Math.max(
      0,
      (page?.freeTotal ?? 3) - (page?.freeUsed ?? 0)
   );
   const { data: customers = [] } = useQuery({
      queryKey: ['isp-customers'],
      queryFn: getIspCustomers,
   });
   const { data: plans = [] } = useQuery({
      queryKey: ['isp-plans'],
      queryFn: getIspPlans,
   });
   const { data: servers = [] } = useQuery({
      queryKey: ['isp-servers'],
      queryFn: getIspServers,
   });

   const customerName = (id: string) =>
      customers.find((c) => c.id === id)?.fullName ?? '—';
   const planName = (id: string) => plans.find((p) => p.id === id)?.name ?? '—';
   const serverName = (id: string) =>
      servers.find((s) => s.id === id)?.name ?? '—';

   const refresh = () => {
      qc.invalidateQueries({ queryKey: ['isp-accounts'] });
      qc.invalidateQueries({ queryKey: ['isp-readiness'] });
   };
   const msg = (e: unknown, fallback: string) =>
      (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
      fallback;

   const suspend = useMutation({
      mutationFn: (v: { id: string; reason?: string }) =>
         suspendIspAccount(v.id, v.reason),
      onSuccess: (r: { warning?: string }) => {
         refresh();
         handleClose();
         /* 🔴 A QUEUED CHANGE IS NOT A DONE ONE. With the tunnel blocked the
            change is stored and the router collects it — see road 2 — but an
            operator told "Suspended" would believe the line is already cut.
            The warning says which it was, and it is a warning, not a success. */
         if (r?.warning) toast.warning(r.warning);
         else
            toast.success(
               'Suspended. Their login is kept, so resuming is one click.'
            );
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not suspend it.')),
   });

   const resume = useMutation({
      mutationFn: (id: string) => resumeIspAccount(id),
      onSuccess: (r: { warning?: string }) => {
         refresh();
         /* The one that matters most: they have PAID. Telling the operator
            "Back online" when the router never heard us sends them to the
            customer with the wrong answer. */
         if (r?.warning) toast.warning(r.warning);
         else toast.success('Back online.');
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not resume it.')),
   });

   const disconnect = useMutation({
      mutationFn: (id: string) => disconnectIspAccount(id),
      onSuccess: (r) => {
         refresh();
         handleClose();
         // The billing state is the durable half. If the router could not be
         // reached the operator still needs to know the login is still on it.
         if (r.warning) toast.warning(r.warning);
         else toast.success('Disconnected. Their invoices and payments are kept.');
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not disconnect it.')),
   });

   const addAccount = useMutation({
      mutationFn: (draft: IspAccountDraft) => createIspAccount(draft),
      onSuccess: (r) => {
         refresh();
         handleClose();
         if (r.sync.ok) toast.success(r.sync.message);
         else toast.warning(r.sync.message);
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not add the account.')),
   });

   /** Adding here means choosing the customer first — the form assumes one. */
   const openAdd = () =>
      gate('server+plan+customer', () => {
         handleShow({
            template: 'isp-account-picker',
            title: 'New connection',
            windowType: 'desktop',
            data: {
               customers,
               onCancel: handleClose,
               onPick: (c: { id: string; fullName: string }) =>
                  handleShow({
                     template: 'isp-account-form',
                     title: `New account — ${c.fullName}`,
                     windowType: 'desktop',
                     data: {
                        customerId: c.id,
                        customerName: c.fullName,
                        onCancel: handleClose,
                        onSave: (draft: IspAccountDraft) => addAccount.mutate(draft),
                     },
                  }),
            },
         });
      });

   /** Their whole billing history, because a balance alone invites an argument. */
   /**
    * Upgrading, or fixing a login, without tearing the line down.
    *
    * 🔴 The server writes the ROUTER first and our record only if that
    * succeeded, so its error is shown verbatim: a 502 means nothing changed
    * anywhere, and paraphrasing it would hide which half failed.
    */
   const edit = useMutation({
      mutationFn: (v: {
         id: string;
         input: { plan?: string; pppoePassword?: string; dueDay?: number };
      }) => updateIspAccount(v.id, v.input),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-accounts'] });
         handleClose();
         toast.success(r.message);
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not save that.'
         ),
   });

   const openEdit = (a: IspAccountRow) =>
      handleShow({
         template: 'isp-account-edit',
         title: `Edit — ${customerName(a.customer)}`,
         windowType: 'desktop',
         data: {
            account: a,
            customerName: customerName(a.customer),
            currentPlanName: planName(a.plan),
            serverName: serverName(a.server),
            onCancel: handleClose,
            onSave: (input: {
               plan?: string;
               pppoePassword?: string;
               dueDay?: number;
            }) => edit.mutate({ id: a.id, input }),
         },
      });

   /**
    * Money taken at the counter, or a GCash the operator saw in their own app.
    *
    * The portal is the EXCEPTION — most subscribers pay in person — so every
    * place a bill is listed has to be able to record one (Ray, 2026-09-01).
    */
   const pay = useMutation({
      mutationFn: (draft: IspPaymentDraft) => createIspPayment(draft),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-accounts'] });
         qc.invalidateQueries({ queryKey: ['isp-invoices'] });
         handleClose();
         // Overpayment is the operator's to resolve with the customer, so it
         // is a warning they must read rather than a quiet success.
         if (r.unapplied > 0) toast.warning(r.message);
         else toast.success(r.message);
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not record the payment.'
         ),
   });

   const openPayment = (a: IspAccountRow, outstanding: number) =>
      handleShow({
         template: 'isp-payment-form',
         title: `Record payment — ${customerName(a.customer)}`,
         windowType: 'desktop',
         data: {
            customerId: a.customer,
            customerName: customerName(a.customer),
            outstanding,
            onCancel: handleClose,
            onSave: (draft: IspPaymentDraft) => pay.mutate(draft),
         },
      });

   const openInvoices = (a: IspAccountRow) =>
      handleShow({
         template: 'isp-account-invoices',
         title: '',
         windowType: 'desktop',
         data: {
            customerId: a.customer,
            customerName: customerName(a.customer),
            onCancel: handleClose,
            onPay: (inv: { outstanding: number }) =>
               openPayment(a, inv.outstanding),
         },
      });

   const confirmSuspend = (a: IspAccountRow) =>
      handleShow({
         template: 'isp-confirm',
         title: '',
         windowType: 'desktop',
         data: {
            title: `Suspend ${customerName(a.customer)}?`,
            message:
               'Their connection stops, but their PPPoE login stays on the router — resuming is one click. Use this for somebody who is late paying.',
            confirmLabel: 'Suspend',
            // 🔴 The customer reads this on their bill page. Without it,
            // somebody suspended by hand with nothing owed is told their
            // account is "fully settled" — true, useless, and the exact
            // confusion Ray hit on 2026-09-02.
            notePrompt: 'Why? (the customer sees this)',
            noteHint:
               'e.g. "Please call the office" or "Awaiting payment arrangement". Leave blank for late payment.',
            onConfirm: (reason?: string) =>
               suspend.mutateAsync({ id: a.id, reason }),
            onCancel: handleClose,
         },
      });

   const confirmDisconnect = (a: IspAccountRow) =>
      handleShow({
         template: 'isp-confirm',
         title: '',
         windowType: 'desktop',
         data: {
            title: `Disconnect ${customerName(a.customer)}?`,
            message:
               'Use this when they have LEFT. Their login is removed from the router so nobody else can use it, and every invoice and payment is kept. If they are only late paying, suspend instead.',
            confirmLabel: 'Disconnect',
            onConfirm: () => disconnect.mutateAsync(a.id),
            onCancel: handleClose,
         },
      });

   /**
    * Days until this line's next bill day.
    *
    * Ray, 2026-09-01: *"in actions items, its nice if it shows in the column
    * the days countdown."* "Day 5" alone makes an operator work out today's
    * date and count; the number of days is the thing they actually wanted.
    */
   const daysToBill = (dueDay: number) => {
      const now = new Date();
      const thisMonth = new Date(
         now.getFullYear(),
         now.getMonth(),
         dueDay
      ).setHours(0, 0, 0, 0);
      const today = new Date().setHours(0, 0, 0, 0);
      if (thisMonth >= today) {
         return Math.round((thisMonth - today) / 86400000);
      }
      // Past this month's day — count to next month's. Using the same day
      // number rather than +30 keeps it right across short months.
      const next = new Date(
         now.getFullYear(),
         now.getMonth() + 1,
         dueDay
      ).setHours(0, 0, 0, 0);
      return Math.round((next - today) / 86400000);
   };

   /*
    * The countdown to their NEXT bill, shown when nothing is outstanding.
    *
    * 🔴 IT SAYS "BILLS", BECAUSE NOTHING IS DUE YET. Ray, 2026-09-04, on a line
    * with no invoice raised: *"days left is correct?"* It read a bare "today"
    * beside "no bill due" and "settled" — three columns about the same bill,
    * one of them sounding urgent about a bill that does not exist.
    *
    * "Bills today" is the same fact without the alarm: their bill day is today,
    * and the hourly run has not reached it yet.
    */
   /** 1st, 2nd, 3rd, 4th — said the way an operator reads a date aloud. */
   const ordinalDay = (n: number) => {
      const t = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return `${n}${t[(v - 20) % 10] ?? t[v] ?? t[0]}`;
   };

   /** "4th of Oct" — the real date the next bill lands. */
   const billDateLabel = (a: IspAccountRow) => {
      if (!a.nextBillAt) return `day ${a.dueDay}`;
      const d = new Date(a.nextBillAt);
      const day = d.getUTCDate();
      const month = d.toLocaleDateString('en-PH', {
         month: 'short',
         timeZone: 'UTC',
      });
      return `${ordinalDay(day)} of ${month}`;
   };

   /** Days from today until that date, counted whole. */
   const daysToNextBill = (a: IspAccountRow) => {
      if (!a.nextBillAt) return daysToBill(a.dueDay);
      const d = new Date(a.nextBillAt);
      const target = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      const now = new Date();
      const today = Date.UTC(
         now.getUTCFullYear(),
         now.getUTCMonth(),
         now.getUTCDate()
      );
      return Math.max(0, Math.round((target - today) / 86400000));
   };

   /** The countdown, driven by the server's date rather than a guess. */
   const billCountdown = (a: IspAccountRow) => {
      const d = daysToNextBill(a);
      return d === 0
         ? 'bills today'
         : d === 1
           ? 'bills tomorrow'
           : `bills in ${d} days`;
   };

   const billLabel = (dueDay: number) => {
      const d = daysToBill(dueDay);
      return d === 0
         ? 'bills today'
         : d === 1
           ? 'bills tomorrow'
           : `bills in ${d} days`;
   };

   /**
    * Is this line up right now?
    *
    * The sampler touches every live session every five minutes, so anything
    * seen within ten is connected. Longer than that and it has gone — a
    * threshold, not a guess: one missed poll must not report an outage.
    */
   const isLive = (a: IspAccountRow) =>
      !!a.lastOnlineAt &&
      Date.now() - new Date(a.lastOnlineAt).getTime() < 10 * 60 * 1000;

   /** '4.2 GB', '860 MB' — the units an operator actually says out loud. */
   const gb = (bytes?: number) => {
      const b = bytes ?? 0;
      if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
      if (b >= 1e6) return `${Math.round(b / 1e6)} MB`;
      if (b > 0) return `${Math.round(b / 1e3)} KB`;
      // "0 GB" in its own column, not a sentence: the columns beside it are
      // numbers, and a column of numbers with prose in it cannot be scanned.
      return '0 GB';
   };

   const clock = (iso: string) =>
      new Date(iso).toLocaleString('en-PH', {
         day: 'numeric',
         month: 'short',
         hour: 'numeric',
         minute: '2-digit',
      });

   const counts = useMemo(() => {
      const c = { all: accounts.length, waiting: 0, active: 0, suspended: 0, disconnected: 0 };
      accounts.forEach((a) => c[a.status]++);
      return c;
   }, [accounts]);

   /**
    * Suspended lines get their own table, above the list.
    *
    * Ray, 2026-09-02: *"the suspended accounts should be separate table like a
    * pending customer, its easier to notice."* Same reasoning as the pending
    * queue: a suspended customer is WORK — somebody is off the internet and
    * owes money — not a row to scroll past. Mixed in, it is only visible to
    * whoever thinks to filter for it.
    */
   const suspendedRows = useMemo(
      () => accounts.filter((a) => a.status === 'suspended'),
      [accounts]
   );

   /**
    * 🔴 RESET ON EVERY FILTER CHANGE. Ray, 2026-09-03: *"make sure accounts,
    * customers, billing has pagination."* Without the reset, filtering from
    * 300 rows down to 4 while on page 6 shows an empty table and an operator
    * who believes the filter found nothing.
    */
   const [pageNo, setPageNo] = useState(0);

   const shown = useMemo(() => {
      const q = query.trim().toLowerCase();
      return accounts.filter((a) => {
         // Shown in its own table above; twice over would make the counts
         // disagree with what an operator is looking at.
         if (a.status === 'suspended' && filter === 'all') return false;
         if (filter !== 'all' && a.status !== filter) return false;
         if (!q) return true;
         // A cashier searches by whatever the customer says on the phone.
         return (
            customerName(a.customer).toLowerCase().includes(q) ||
            a.pppoeUsername.toLowerCase().includes(q)
         );
      });
   }, [accounts, filter, query, customers]);

   const paged = useMemo(
      () => shown.slice(pageNo * PAGE_SIZE, (pageNo + 1) * PAGE_SIZE),
      [shown, pageNo]
   );

   // A filter that narrows the list must take the reader back to its start,
   // or they land on a page that no longer exists and read it as "nothing
   // found".
   useEffect(() => {
      setPageNo(0);
   }, [shown.length]);

   return (
      <IspPage>
         <TopRow>
            <Title>Accounts</Title>
            <AddRow>
               <Primary type="button" onClick={openAdd}>
                  + Add account
               </Primary>
               {/* 🔴 Ray, 2026-09-03: *"in add account button, beside it has
                   label free 3/3."* It is the only place the allowance is
                   actionable — the moment before they create one is when
                   knowing costs nothing and afterwards it is spent for ever. */}
               {/* 🔴 GONE once it is spent. Ray, 2026-09-03: *"if no free
                   left. dont display this."* A badge that only ever says "no"
                   is a permanent reminder of something the operator can do
                   nothing about — and it sits beside the button they press
                   every time they add a customer. */}
               {freeLeft > 0 ? (
                  <FreeLeft>free {freeLeft}/3 left</FreeLeft>
               ) : null}
            </AddRow>
         </TopRow>
         {/* ACCOUNT is Aztech's word and Ray corrected it into the product
             himself — a restaurant owner with Resto 1 and Resto 2 is ONE
             customer with TWO accounts. "Connection" reads more plainly but it
             is not what an operator says, so it stays in the explanation and
             out of the labels. */}
         <Note>
            An account is one plan, on one router, for one customer. Somebody with
            two branches holds two — each with its own bill day.
         </Note>

         {suspendedRows.length > 0 && (
            <Suspended>
               <SuspHead>
                  <SuspTitle>
                     {suspendedRows.length} customer
                     {suspendedRows.length === 1 ? '' : 's'} cut off
                  </SuspTitle>
                  <SuspNote>
                     They are on the walled garden and can see their bill. They
                     come back the moment a payment is recorded.
                  </SuspNote>
               </SuspHead>
               <TableWrap>
                  <Table>
                     <thead>
                        <tr>
                           <Th>Customer</Th>
                           <Th>Plan</Th>
                           <Th>PPPoE login</Th>
                           <Th>Owes</Th>
                           <Th />
                        </tr>
                     </thead>
                     <tbody>
                        {suspendedRows.map((a) => (
                           <tr key={a.id}>
                              <Td $bold>{customerName(a.customer)}</Td>
                              <Td>{planName(a.plan)}</Td>
                              <Td $mono>
                              <CellStack>
                                 <span>{a.pppoeUsername}</span>
                                 {/* The name read down a phone. See ispCodes. */}
                                 <CodeSub>{a.accountCode}</CodeSub>
                              </CellStack>
                           </Td>
                              <Td>
                                 {a.balance > 0 ? (
                                    <Owes $late={a.overdue}>
                                       ₱{a.balance.toLocaleString()}
                                    </Owes>
                                 ) : (
                                    // Suspended with nothing owed happens when
                                    // an operator does it by hand. Saying so
                                    // beats an empty cell.
                                    <Clear>settled</Clear>
                                 )}
                              </Td>
                              <Td>
                                 <RowActions>
                                    <LinkBtn
                                       type="button"
                                       disabled={resume.isPending}
                                       onClick={() => resume.mutate(a.id)}
                                    >
                                       Resume
                                    </LinkBtn>
                                    <LinkBtn
                                       type="button"
                                       onClick={() => openInvoices(a)}
                                    >
                                       Invoices
                                    </LinkBtn>
                                 </RowActions>
                              </Td>
                           </tr>
                        ))}
                     </tbody>
                  </Table>
               </TableWrap>
               <IspPager
                  page={pageNo}
                  total={shown.length}
                  onPage={setPageNo}
                  noun="accounts"
               />
            </Suspended>
         )}

         <Bar>
            <Search
               value={query}
               placeholder="Search customer or PPPoE username"
               onChange={(e) => setQuery(e.target.value)}
            />
            {/* 🔴 JUST TWO. Ray, 2026-09-02: *"in the main table just [all,
                disconnected]."* Suspended lines have their own table above, so
                a chip for them is a second place to find the same thing.
                Active is what "All" already shows — a chip that filters
                nothing out only invites a click that does nothing. */}
            {(['all', 'disconnected'] as Filter[]).map(
               (f) => (
                  <Chip
                     key={f}
                     type="button"
                     $on={filter === f}
                     onClick={() => setFilter(f)}
                  >
                     {f === 'all' ? 'All' : STATUS_LABEL[f]} ({counts[f]})
                  </Chip>
               )
            )}
         </Bar>

         {isLoading && <SkeletonLines lines={3} />}

         {!isLoading && accounts.length === 0 && (
            <EmptyState>
               <EmptyBig>No accounts yet</EmptyBig>
               <EmptySub>
                  An account is what a customer actually pays for. Add one and
                  SmartFi writes the PPPoE login onto the router for you.
               </EmptySub>
               <Primary type="button" onClick={openAdd}>
                  + Add account
               </Primary>
            </EmptyState>
         )}

         {!isLoading && accounts.length > 0 && shown.length === 0 && (
            <EmptyNote>Nothing matches that.</EmptyNote>
         )}

         {shown.length > 0 && (
            <TableWrap>
               <Table>
                  <thead>
                     <tr>
                        <Th>Customer</Th>
                        <Th>Plan</Th>
                        <Th>MikroTik</Th>
                        <Th>PPPoE login</Th>
                        {/* 🔴 What an operator rings up about. Ray,
                            2026-09-02: "nice if we show bandwidth usage."
                            "Connected since" beats a green dot, because the
                            question is always "has it been dropping?" */}
                        {/* 🔴 ONE FACT PER COLUMN. Ray, 2026-09-03: *"why you
                            need to mix all here? why not put separate
                            column?"* Four readings stacked in one cell cannot
                            be scanned down — an operator looking for the busy
                            line has to read every row instead of running an
                            eye down a column. */}
                        <Th>Connection</Th>
                        <Th>Today</Th>
                        <Th>This month</Th>
                        <Th>All time</Th>
                        <Th>Subscription</Th>
                        <Th>Bills on</Th>
                        <Th>Days left</Th>
                        {/* 🔴 THE GRACE WAS INVISIBLE. Ray, 2026-09-04, after a
                            bill went past due and his subscriber stayed online:
                            *"so how the operator knows the grace period
                            remaining. you should add that in columns."*
                            Auto-suspend was behaving correctly — the plan
                            carries three days — but nothing said so, which made
                            it look broken and invites cutting somebody off by
                            hand. Its own column, not crammed into Days left. */}
                        <Th>Grace ends</Th>
                        <Th>Balance</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                     </tr>
                  </thead>
                  <tbody>
                     {paged.map((a) => (
                        <tr key={a.id}>
                           <Td $bold>{customerName(a.customer)}</Td>
                           <Td>{planName(a.plan)}</Td>
                           <Td $muted>{serverName(a.server)}</Td>
                           <Td $mono>{a.pppoeUsername}</Td>
                           <Td>
                              {/* 🔴 Ray asked "where is the monitoring
                                  columns?" TWICE while looking straight at
                                  this one. The data was right and the
                                  presentation was not: three grey lines of
                                  prose read as a note, not as a reading. A dot
                                  and a number read as an instrument. */}
                              <CellStack>
                                 <LiveRow>
                                    <LiveDot $on={isLive(a)} />
                                    <Live $on={isLive(a)}>
                                       {isLive(a) ? 'Online' : 'Offline'}
                                    </Live>
                                 </LiveRow>
                                 <CellSub>
                                    {isLive(a) && a.onlineSince
                                       ? `up ${clock(a.onlineSince)}`
                                       : a.lastOnlineAt
                                         ? `last seen ${clock(a.lastOnlineAt)}`
                                         : 'never connected'}
                                 </CellSub>
                              </CellStack>
                           </Td>
                           {/* Today, month, lifetime — each in its own column,
                               so a heavy line can be found by running an eye
                               down one of them. */}
                           <Td>
                              <Usage $none={!a.usageTodayBytes}>
                                 {gb(a.usageTodayBytes)}
                              </Usage>
                           </Td>
                           <Td $muted={!a.usageBytes}>{gb(a.usageBytes)}</Td>
                           <Td $muted={!a.usageLifetimeBytes}>
                              {gb(a.usageLifetimeBytes)}
                           </Td>
                           <Td>
                              {/* "Standard", never "paid": paid reads as a
                                  payment state, which is the Balance column's
                                  question, not this one. */}
                              {a.isFree ? (
                                 <FreePill>free</FreePill>
                              ) : (
                                 <PlainPill>standard</PlainPill>
                              )}
                           </Td>
                           {/* The date only. The countdown used to live here
                               too, and then Days left said "—" beside "in 28
                               days" — two columns disagreeing about the same
                               question. One countdown, one column. */}
                           {/* Ray, 2026-09-04: *"the value of 'Bills on' to
                               better understand is = ex. '4th of Oct'"*. A day
                               number made an operator work out the date; and it
                               is the SERVER's date, because a line that skips
                               its first month cannot be worked out here. */}
                           <Td $muted>{billDateLabel(a)}</Td>
                           {/* 🔴 Days to the SOONEST open bill, not the newest.
                               Ray, 2026-09-03: *"put a column for days left."*
                               What an operator is counting down to is the
                               moment this person gets cut off, and that is
                               whichever bill lands first. */}
                           <Td>
                              {/*
                                 🔴 THREE CASES, NOT TWO. Written as two nested
                                 ternaries it rendered BOTH countdowns at once —
                                 "4d overduein 27 days" on Ray's own screen,
                                 2026-09-03 — because a line whose debt IS its
                                 own fell through to the settled branch.
                              */}
                              {typeof a.daysLeft !== 'number' ? (
                                 /* Settled: the wait for their NEXT bill. A
                                    dash read as broken beside a "Bills on"
                                    column that was already counting. */
                                 <Days $tone="ok">{billCountdown(a)}</Days>
                              ) : (
                                 <>
                                    <Days
                                       $tone={
                                          a.daysLeft < 0
                                             ? 'late'
                                             : a.daysLeft <= 3
                                               ? 'soon'
                                               : 'ok'
                                       }
                                    >
                                       {a.daysLeft < 0
                                          ? `${Math.abs(a.daysLeft)}d overdue`
                                          : a.daysLeft === 0
                                            ? 'due today'
                                            : `${a.daysLeft}d`}
                                    </Days>
                                    {/* Whose debt it is. An invoice belongs to
                                        the customer, so it shows on every line
                                        they have — but "4d overdue" on a line
                                        that has never been billed reads as an
                                        accusation against that line. */}
                                    {!a.daysLeftIsOwn ? (
                                       <CellSub>on another line</CellSub>
                                    ) : null}
                                 </>
                              )}
                           </Td>
                           <Td>
                              {a.cutoffInDays == null || a.balance <= 0 ? (
                                 /* Nothing owed, so nothing is counting down —
                                    but NEVER a dash. A dash here read as broken
                                    once already, beside a column that WAS
                                    counting; every cell owes an answer. */
                                 <Clear>no bill due</Clear>
                              ) : a.cutoffInDays === 0 ? (
                                 <Days $tone="late">today</Days>
                              ) : (
                                 <CellStack>
                                    {/* Ray, 2026-09-04, named this one:
                                        *"the columns should 'Grace Ends' =
                                        ${graceLeft} days"*. Days, spelled out
                                        — the abbreviation was mine. */}
                                    <Days $tone={a.cutoffInDays <= 1 ? 'late' : 'soon'}>
                                       {a.cutoffInDays}{' '}
                                       {a.cutoffInDays === 1 ? 'day' : 'days'}
                                    </Days>
                                    <CellSub>of {a.graceDays}d grace</CellSub>
                                 </CellStack>
                              )}
                           </Td>
                           {/* Per CUSTOMER, not per account — one invoice can
                               cover two of somebody's lines, so splitting it
                               would invent a number that appears on no bill. */}
                           <Td>
                              {a.balance > 0 ? (
                                 <Owes $late={a.overdue}>
                                    ₱{a.balance.toLocaleString()}
                                    {a.overdue && <LateTag>overdue</LateTag>}
                                 </Owes>
                              ) : (
                                 <Clear>settled</Clear>
                              )}
                           </Td>
                           <Td>
                              <Pill $s={a.status}>{STATUS_LABEL[a.status]}</Pill>
                              {a.lastError && (
                                 <ErrLine
                                    type="button"
                                    title={a.lastError}
                                    onClick={() => toast.warning(a.lastError)}
                                 >
                                    Router problem
                                 </ErrLine>
                              )}
                           </Td>
                           <Td>
                              <RowActions>
                                 {a.status === 'suspended' ? (
                                    <LinkBtn
                                       type="button"
                                       disabled={resume.isPending}
                                       onClick={() => resume.mutate(a.id)}
                                    >
                                       Resume
                                    </LinkBtn>
                                 ) : a.status !== 'disconnected' ? (
                                    <LinkBtn
                                       type="button"
                                       onClick={() => confirmSuspend(a)}
                                    >
                                       Suspend
                                    </LinkBtn>
                                 ) : null}
                                 {a.status !== 'disconnected' ? (
                                    <LinkBtn
                                       type="button"
                                       onClick={() => openEdit(a)}
                                    >
                                       Edit
                                    </LinkBtn>
                                 ) : null}
                                 <LinkBtn
                                    type="button"
                                    onClick={() => openInvoices(a)}
                                 >
                                    Invoices
                                 </LinkBtn>
                                 {/* 🔴 NO DISCONNECT HERE. Ray, 2026-09-03:
                                     *"please remove that disconnect to avoid
                                     those logs dialing, and suspended is good
                                     already."*

                                     Disconnect REMOVES the PPPoE login. A
                                     modem that is still plugged in then fails
                                     authentication and retries every few
                                     seconds, for ever — filling RouterOS's
                                     1000-line memory log and flushing out
                                     every entry an operator would need to
                                     diagnose anything else.

                                     Suspend does the job without that: the
                                     login stays, the customer lands on the
                                     walled garden, and resuming is one click.
                                     The route still exists for a customer who
                                     has genuinely gone; it is simply not a
                                     button beside Suspend, where it was one
                                     mis-click from a log nobody can read. */}
                              </RowActions>
                           </Td>
                        </tr>
                     ))}
                  </tbody>
               </Table>
            </TableWrap>
         )}
      </IspPage>
   );
};

/** Reddens inside three days — that is when an operator wants to look. */
const Countdown = styled.div<{ $soon: boolean }>`
   font-size: 11px;
   font-weight: ${({ $soon }) => ($soon ? 800 : 600)};
   color: ${({ $soon }) => ($soon ? colors.PRIMARY : colors.GRAY5)};
`;

/**
 * Given a colour and a count because it is WORK, not data — the same treatment
 * as the pending-application queue, for the same reason.
 */
const Suspended = styled.div`
   border: 1px solid ${colors.RED};
   border-radius: 12px;
   padding: 14px 14px 4px;
   margin-bottom: 20px;
   background: rgba(220, 53, 69, 0.04);
`;
const SuspHead = styled.div`
   margin-bottom: 10px;
`;
const SuspTitle = styled.div`
   font-size: 15px;
   font-weight: 800;
`;
const SuspNote = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const CellStack = styled.div`
   display: flex;
   flex-direction: column;
   gap: 1px;
`;
const CellSub = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const Live = styled.span<{ $on: boolean }>`
   font-size: 12px;
   font-weight: 800;
   color: ${({ $on }) => ($on ? colors.GREEN : colors.GRAY5)};
`;

export default IspAccounts;

const TopRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const Title = styled.h1`
   margin: 0;
   font-size: 20px;
`;

const Note = styled.p`
   margin: 0;
   font-size: 12.5px;
   line-height: 1.6;
   color: ${colors.GRAY5};
   max-width: 70ch;
`;

const Bar = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   flex-wrap: wrap;
`;

const Search = styled.input`
   flex: 1 1 260px;
   padding: 8px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
`;

const Chip = styled.button<{ $on: boolean }>`
   padding: 6px 12px;
   border-radius: 20px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;
   border: 1px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
   color: ${({ $on }) => ($on ? colors.WHITE : colors.GRAY3)};
`;

const TableWrap = styled.div`
   overflow-x: auto;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
   ${actionColumn}
`;

const Th = styled.th`
   text-align: left;
   padding: 10px 12px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${colors.GRAY14};
   white-space: nowrap;
`;

const Td = styled.td<{ $bold?: boolean; $muted?: boolean; $mono?: boolean }>`
   padding: 10px 12px;
   border-top: 1px solid ${colors.GRAY13};
   color: ${({ $muted }) => ($muted ? colors.GRAY5 : colors.MAIN_DARK)};
   font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
   font-family: ${({ $mono }) => ($mono ? 'monospace' : 'inherit')};
   white-space: nowrap;
`;

/** Waiting is amber, not red: nothing is broken, the router has not taken it yet. */
const Pill = styled.span<{ $s: IspAccountRow['status'] }>`
   display: inline-block;
   padding: 2px 10px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
   background: ${({ $s }) =>
      $s === 'active'
         ? `${colors.SUCCESS}18`
         : $s === 'suspended'
           ? `${colors.ORANGE}18`
           : $s === 'disconnected'
             ? `${colors.GRAY12}`
             : `${colors.ORANGE}18`};
   color: ${({ $s }) =>
      $s === 'active'
         ? colors.SUCCESS
         : $s === 'disconnected'
           ? colors.GRAY5
           : colors.ORANGE};
`;

// A button, not the sentence itself: the whole reason in this narrow column
// stretched one row to nine lines (Ray 2026-11-02). Tap reads it, phones included.
const ErrLine = styled.button`
   display: block;
   margin-top: 3px;
   padding: 0;
   border: 0;
   background: none;
   font-size: 10.5px;
   font-weight: 600;
   line-height: 1.4;
   color: ${colors.DANGER};
   white-space: nowrap;
   text-decoration: underline;
   cursor: pointer;
`;

const AddRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;
/** Quiet until it is gone: a badge shouting at a full allowance is noise. */
const FreeLeft = styled.span`
   font-size: 11.5px;
   font-weight: 700;
   padding: 4px 10px;
   border-radius: 999px;
   white-space: nowrap;
   border: 1px solid #a7f3d0;
   background: #ecfdf5;
   color: #047857;
`;
/** The same green as the badge, so "free" reads as one idea across the page. */
const PlainPill = styled.span`
   display: inline-block;
   font-size: 10.5px;
   font-weight: 800;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   padding: 2px 7px;
   border-radius: 999px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.GRAY14};
   color: ${colors.GRAY5};
`;
const FreePill = styled.span`
   display: inline-block;
   font-size: 10.5px;
   font-weight: 800;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   padding: 2px 7px;
   border-radius: 999px;
   border: 1px solid #a7f3d0;
   background: #ecfdf5;
   color: #047857;
`;
const CodeSub = styled.span`
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   font-size: 10.5px;
   letter-spacing: -0.01em;
   color: ${colors.GRAY5};
`;
const LiveRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
`;
const LiveDot = styled.span<{ $on: boolean }>`
   width: 7px;
   height: 7px;
   border-radius: 50%;
   flex: none;
   background: ${({ $on }) => ($on ? colors.SUCCESS : colors.GRAY12)};
`;
/** The number an operator is actually scanning for. */
const Usage = styled.div<{ $none: boolean }>`
   margin-top: 2px;
   font-size: 12.5px;
   font-weight: ${({ $none }) => ($none ? 400 : 700)};
   color: ${({ $none }) => ($none ? colors.GRAY5 : colors.MAIN_DARK)};
`;
const UsageSub = styled.span`
   font-weight: 400;
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const Days = styled.span<{ $tone: 'ok' | 'soon' | 'late' }>`
   font-weight: ${({ $tone }) => ($tone === 'ok' ? 400 : 700)};
   color: ${({ $tone }) =>
      $tone === 'late'
         ? colors.DANGER
         : $tone === 'soon'
           ? colors.ORANGE
           : colors.GRAY5};
`;
const Primary = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
`;

const LinkBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const DangerBtn = styled.button`
   background: transparent;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER}40;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
`;

const EmptyNote = styled.p`
   margin: 0;
   padding: 20px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const EmptyState = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   padding: 40px 22px;
   text-align: center;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
`;

const EmptyBig = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const EmptySub = styled.p`
   margin: 0 0 6px;
   max-width: 440px;
   font-size: 12px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;

/** What the customer owes, and whether any of it is late. */
const Owes = styled.span<{ $late: boolean }>`
   font-weight: 700;
   color: ${({ $late }) => ($late ? colors.DANGER : colors.MAIN_DARK)};
`;

const LateTag = styled.span`
   margin-left: 6px;
   padding: 1px 6px;
   border-radius: 8px;
   font-size: 10px;
   font-weight: 700;
   background: ${colors.DANGER}18;
   color: ${colors.DANGER};
`;

const Clear = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
